import { useEffect, useState } from 'react';

/**
 * The subset of global browser APIs this module needs to serve binary payloads.
 *
 * Injectable so object URLs can be minted in another window (e.g. a popover's
 * pop-out) and so tests can supply fakes for `Blob` and the object-URL APIs.
 */
export type PdfObjectUrlGlobal = {
	Blob: typeof Blob;
	URL: Pick<typeof URL, 'createObjectURL' | 'revokeObjectURL'>;
};

const defaultPdfObjectUrlGlobal = globalThis as unknown as PdfObjectUrlGlobal;

/**
 * Converts a browser Blob into a serializable data URL.
 *
 * Use this for persisted image payloads; object URLs are process-local and cannot
 * be restored from IndexedDB after a reload.
 *
 * @param blob - The blob to encode.
 * @returns A promise resolving to the blob's `data:` URL.
 * @throws If the blob cannot be read or does not decode to a string.
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => {
			if (typeof reader.result !== 'string') {
				reject(new Error('Blob did not convert to a data URL'));
				return;
			}

			resolve(reader.result);
		};

		reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
		reader.readAsDataURL(blob);
	});
}

/**
 * Creates a temporary PDF object URL from raw file bytes.
 *
 * Prefer `usePdfObjectUrlFromBytes` in React components so the URL is revoked.
 * The caller owns the returned URL and must revoke it to avoid leaking memory.
 *
 * @param contents - The PDF bytes.
 * @param objectUrlGlobal - Browser APIs to use; defaults to the global ones, overridable in tests.
 * @returns A `blob:` object URL referencing the bytes.
 */
export function createPdfObjectUrlFromBytes(
	contents: ArrayBuffer,
	objectUrlGlobal: PdfObjectUrlGlobal = defaultPdfObjectUrlGlobal,
): string {
	return objectUrlGlobal.URL.createObjectURL(new objectUrlGlobal.Blob([contents], { type: 'application/pdf' }));
}

/** A PDF object URL and the bytes it was minted from. */
interface MintedPdfObjectUrl {
	contents: ArrayBuffer;
	objectUrl: string;
}

/**
 * Creates and cleans up a temporary PDF object URL for raw file bytes.
 *
 * The URL is revoked when the contents change or the component unmounts, so callers need not manage
 * its lifecycle. It is undefined until the first commit with the current contents, and never one
 * minted for earlier contents.
 *
 * @param contents - The PDF bytes, or `undefined` when none are available.
 * @param objectUrlGlobal - Browser APIs to use; defaults to the global ones, overridable in tests.
 * @returns The current `blob:` object URL, or `undefined` when there is none yet.
 */
export function usePdfObjectUrlFromBytes(
	contents: ArrayBuffer | undefined,
	objectUrlGlobal: PdfObjectUrlGlobal | null = defaultPdfObjectUrlGlobal,
): string | undefined {
	const [minted, setMinted] = useState<MintedPdfObjectUrl | null>(null);

	useEffect(() => {
		if (!contents) {
			return;
		}

		const urlGlobal = objectUrlGlobal ?? defaultPdfObjectUrlGlobal;
		const objectUrl = createPdfObjectUrlFromBytes(contents, urlGlobal);
		setMinted({ contents, objectUrl });
		return () => urlGlobal.URL.revokeObjectURL(objectUrl);
	}, [contents, objectUrlGlobal]);

	return minted !== null && minted.contents === contents ? minted.objectUrl : undefined;
}
